import { useState } from "react";

import {
  getDefaultBuildingId,
  getUser,
  handleError,
  useAppDispatch,
  useAppSelector,
  withAsyncThunkErrorHandling,
} from "@lib/store";
import { actions } from "@lib/store/src/data/timeslots/actions";
import {
  getIsOfficeDayBookableBasedOnQuota,
  getOfficeDayDisabledMessage,
  getScheduleForDateIsUpdating,
} from "@lib/store/src/data/timeslots/selectors/index";
import { localDateStringToEndOfDay, localDateStringToStartOfDay } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { WorkdayStatus } from "@lib/store/src/data/timeslots/apiTypes";
import { WorkdayStatusButton } from "./WorkdayStatusButton";
import { constructOfficeWorkdayDraft, isBeforeToday, usePreselectTimeslot } from "@lib/logic";
import { useMemoDate } from "@lib/store/src/ui/hooks/useMemoDate";
import { useModal } from "@hooks";
import { BookWorkspaceCard, BookingLocationStatusCard } from "../../bookWorkspace";
import {
  getBuildingIdsThatRequireWorkspaceBooking,
  getBuildingSections,
} from "@lib/store/src/data/timeslots/selectors/getBuildingSections";
import { isOfficeWorkday } from "@lib/store/src/data/timeslots/utils";

interface NoWorkdayContentProps {
  selectedDate: string;
}

export const NoWorkdayContent = ({ selectedDate }: NoWorkdayContentProps) => {
  const dispatch = useAppDispatch();
  const { setModalPages, openModal } = useModal();
  const date = useMemoDate(selectedDate);

  const isPast = isBeforeToday(selectedDate);
  const isOfficeDayBookableBasedOnQuota = useAppSelector(state => getIsOfficeDayBookableBasedOnQuota(state, date));
  const officeDayDisabledMessage = useAppSelector(state => getOfficeDayDisabledMessage(state, date));
  const buildingSections = useAppSelector(state => getBuildingSections(state, selectedDate));

  const [localStatus, setLocalStatus] = useState<WorkdayStatus | null>(null);
  const user = useAppSelector(getUser);

  const mutating = useAppSelector(getScheduleForDateIsUpdating(selectedDate));
  const defaultBuildingId = useAppSelector(getDefaultBuildingId);
  const buildingIdsWithRequiredWorkspaceBooking = useAppSelector(getBuildingIdsThatRequireWorkspaceBooking);
  const preselectTimeslot = usePreselectTimeslot({ dateKey: selectedDate });

  const handlePress = (status: WorkdayStatus) => {
    preselectTimeslot();
    handleWorkdayStatus(status);
  };

  const createWorkdayInDefaultBuilding = async () => {
    const res = await dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.createWorkday({
          status: "OfficeDay",
          buildingId: defaultBuildingId,
          localStart: `${selectedDate}T00:00`,
          localEnd: `${selectedDate}T23:59`,
        }),
      ),
    );

    return res.success && isOfficeWorkday(res.result) ? res.result : null;
  };

  const handleWorkdayStatus = async (status: WorkdayStatus) => {
    setLocalStatus(status);

    const base = {
      localStart: localDateStringToStartOfDay(selectedDate),
      localEnd: localDateStringToEndOfDay(selectedDate),
    };

    if (status === "OfficeDay") {
      if (buildingSections.open.length === 1 && buildingSections.open[0].id === defaultBuildingId) {
        const firstOpenBuilding = buildingSections.open[0];
        const workdayRequiresWorkspace = buildingIdsWithRequiredWorkspaceBooking.has(firstOpenBuilding.id);

        // If there's only one open building and it's the default, create the workday directly
        const nextWorkday = workdayRequiresWorkspace
          ? constructOfficeWorkdayDraft({
              user,
              selectedDate,
              building: firstOpenBuilding,
            })
          : await createWorkdayInDefaultBuilding();

        if (!nextWorkday || !workdayRequiresWorkspace) return;

        setModalPages([<BookWorkspaceCard workday={nextWorkday} />]);
      } else {
        setModalPages([
          <BookingLocationStatusCard
            date={selectedDate}
            workday={null}
          />,
        ]);
      }
      openModal();

      return;
    }

    const { success, result } = await dispatch(
      withAsyncThunkErrorHandling(() => actions.createWorkday({ ...base, status })),
    );

    if (!success) {
      setLocalStatus(null);
      handleError(result, dispatch);
      return;
    }

    setLocalStatus(null);
  };

  if (isPast) {
    return null;
  }

  return (
    <div className="flex gap-3 mt-2 my-4">
      <WorkdayStatusButton
        status={"OfficeDay"}
        disabled={mutating || !!officeDayDisabledMessage || !isOfficeDayBookableBasedOnQuota}
        loading={mutating && localStatus === status}
        buttonClassName="rounded bg-mapiq-black-800*/[0.03] hover:bg-mapiq-black-800*/[0.05]"
        iconClassName={`${mutating ? "opacity-60 cursor-not-allowed" : ""}`}
        onClick={() => handlePress("OfficeDay")}
        customTitle={officeDayDisabledMessage?.title}
        customSubtitle={officeDayDisabledMessage?.subtitle}
      />

      <WorkdayStatusButton
        status={"WorkingRemotely"}
        disabled={mutating}
        loading={mutating && localStatus === status}
        buttonClassName="rounded bg-mapiq-black-800*/[0.03] hover:bg-mapiq-black-800*/[0.05]"
        iconClassName={`${mutating ? "opacity-60 cursor-not-allowed" : ""}`}
        onClick={() => handlePress("WorkingRemotely")}
      />

      <WorkdayStatusButton
        status={"NotWorking"}
        disabled={mutating}
        loading={mutating && localStatus === status}
        buttonClassName="rounded bg-mapiq-black-800*/[0.03] hover:bg-mapiq-black-800*/[0.05]"
        iconClassName={`${mutating ? "opacity-60 cursor-not-allowed" : ""}`}
        onClick={() => handlePress("NotWorking")}
      />
    </div>
  );
};
